import { Listbox, Transition } from "@headlessui/react";
import { Fragment } from "react";
import { BiCheck, BiChevronDown } from "react-icons/bi";

export type TagSelectorOption = {
  id: number;
  name: string;
};

type TagSelectorProps = {
  options: TagSelectorOption[];
  selectedIds: number[];
  onChange: (ids: number[]) => void;
  placeholder?: string;
};

const TagSelector = ({
  options,
  selectedIds,
  onChange,
  placeholder = "Selecione pelo menos uma opção",
}: TagSelectorProps) => {
  const selectedOptions = options.filter((option) => selectedIds.includes(option.id));
  const visibleOptions = selectedOptions.slice(0, 2);
  const hiddenOptionsCount = selectedOptions.length - visibleOptions.length;

  return (
    <Listbox value={selectedIds} onChange={onChange} multiple>
      <div className="relative mt-2 inline-block max-w-full">
        <Listbox.Button className="flex min-h-12 w-fit min-w-0 max-w-full items-center justify-between gap-3 rounded-xl border border-grey300 bg-white px-3 py-2 text-left sm:px-4">
          <div className="flex min-w-0 flex-1 items-center gap-2 overflow-hidden">
            {selectedOptions.length > 0 ? (
              <>
                {visibleOptions.map((option, index) => (
                  <span
                    key={option.id}
                    title={option.name}
                    className={`min-w-0 truncate rounded-full bg-grey200 px-2 py-1 text-sm text-grey800 sm:px-3 ${
                      index === 1 ? "max-[425px]:hidden" : ""
                    }`}
                  >
                    {option.name}
                  </span>
                ))}
                {hiddenOptionsCount > 0 && (
                  <span className="shrink-0 rounded-full bg-grey200 px-2 py-1 text-sm font-semibold text-grey800 max-[425px]:hidden sm:px-3">
                    +{hiddenOptionsCount}
                  </span>
                )}
                {selectedOptions.length > 1 && (
                  <span className="shrink-0 rounded-full bg-grey200 px-2 py-1 text-sm font-semibold text-grey800 min-[426px]:hidden">
                    +{selectedOptions.length - 1}
                  </span>
                )}
              </>
            ) : (
              <span className="truncate text-grey500">{placeholder}</span>
            )}
          </div>
          <BiChevronDown className="size-5 shrink-0 text-grey600" />
        </Listbox.Button>

        <Transition
          as={Fragment}
          leave="transition ease-in duration-100"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <Listbox.Options className="absolute z-20 mt-2 max-h-64 w-full overflow-auto rounded-xl border border-grey200 bg-white p-2 shadow-lg focus:outline-none">
            {options.map((option) => (
              <Listbox.Option
                key={option.id}
                value={option.id}
                className={({ active }) =>
                  `flex cursor-pointer items-center justify-between rounded-lg px-3 py-2 ${
                    active ? "bg-grey200 text-black" : "bg-white text-grey700"
                  }`
                }
              >
                {({ selected }) => (
                  <>
                    <span
                      className={`min-w-0 truncate ${selected ? "font-semibold" : "font-normal"}`}
                      title={option.name}
                    >
                      {option.name}
                    </span>
                    {selected && <BiCheck className="size-5 shrink-0 text-black" />}
                  </>
                )}
              </Listbox.Option>
            ))}
          </Listbox.Options>
        </Transition>
      </div>
    </Listbox>
  );
};

export default TagSelector;
